import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { ApiError, changePassword } from "../api/client";
import { useSession } from "../session";

export function Login(): JSX.Element {
  const { user, login, replaceUser } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user && !user.mustChangePassword) return <Navigate to="/" replace />;

  return (
    <div className="app-scroll-page">
      <Card style={{ width: 400 }}>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          登录万昌 WMS
        </Typography.Title>
        <Typography.Paragraph type="secondary">用你自己的工号。不要和别人共用。</Typography.Paragraph>
        {error ? <Alert style={{ marginBottom: 16 }} type={warning ? "warning" : "error"} showIcon message={error} /> : null}
        {user?.mustChangePassword ? (
          <Form
            layout="vertical"
            requiredMark
            onFinish={(values: { currentPassword: string; next: string; again: string }) => {
              if (values.next !== values.again) {
                setWarning(false);
                setError("两次密码不一样。请再输入一遍。");
                return;
              }
              if (values.next.length < 8) {
                setWarning(false);
                setError("密码少于 8 位。请至少输入 8 位。");
                return;
              }
              setBusy(true);
              void changePassword(values.currentPassword, values.next)
                .then((result) => {
                  replaceUser(result.user);
                })
                .catch((err: unknown) => {
                  setWarning(false);
                  setError(err instanceof Error ? err.message : "现在登不进去。请检查网络或让管理员看一下系统。");
                })
                .finally(() => setBusy(false));
            }}
          >
            <Form.Item label="当前密码" name="currentPassword" rules={[{ required: true, message: "密码还空着。请填写后再登录。" }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item label="新密码" name="next" rules={[{ required: true, message: "密码还空着。请填写后再登录。" }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item label="再输入一次新密码" name="again" rules={[{ required: true, message: "密码还空着。请填写后再登录。" }]}>
              <Input.Password />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={busy}>
              保存新密码并进入工作台
            </Button>
          </Form>
        ) : (
          <Form
            layout="vertical"
            requiredMark
            onFinish={(values: { employeeNo: string; password: string }) => {
              setBusy(true);
              setError(null);
              void login(values.employeeNo.trim(), values.password)
                .catch((err: unknown) => {
                  if (err instanceof ApiError && err.status === 423) {
                    setWarning(true);
                    setError("这个账号暂时不能登录。连续输错太多次，请过 15 分钟再试，或让管理员重置密码。");
                    return;
                  }
                  if (err instanceof ApiError && err.status === 0) {
                    setWarning(false);
                    setError("现在登不进去。右上角若是没连上，请检查网络或让管理员看一下系统。");
                    return;
                  }
                  setWarning(false);
                  setError(err instanceof Error ? err.message : "工号或密码不对，还没有进入工作台。请核对后再试。");
                })
                .finally(() => setBusy(false));
            }}
          >
            <Form.Item label="工号" name="employeeNo" rules={[{ required: true, message: "工号还空着。请填写后再登录。" }]}>
              <Input autoFocus />
            </Form.Item>
            <Form.Item label="密码" name="password" rules={[{ required: true, message: "密码还空着。请填写后再登录。" }]}>
              <Input.Password />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={busy}>
              登录并进入工作台
            </Button>
          </Form>
        )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          密码至少 8 位。连续输错 5 次后，这个账号会停 15 分钟。
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
